import { cn } from "@/lib/cn";

export type StatusTone =
  | "quiet"
  | "neutral"
  | "info"
  | "pending"
  | "go"
  | "alert"
  | "brand";

/**
 * Every status word in the system maps to one of six tones. Keeping the map
 * here means "pending" looks identical whether it is an account, a leave
 * request or a signature.
 *
 * "Active" is grey, not green. Thirty of thirty-four employees are active, and
 * a column of thirty green chips tells the reader nothing while making the four
 * that are not harder to find. Every row still carries a chip, so the column
 * reads as one thing; the ordinary case is simply the quiet one and the colour
 * is spent on the exceptions.
 */
const TONE_BY_STATUS: Record<string, StatusTone> = {
  // Account
  draft: "info",
  pending: "pending",
  active: "neutral",
  rejected: "alert",
  disabled: "neutral",
  // Employment
  probation: "info",
  leave: "brand",
  on_leave: "brand",
  resigned: "neutral",
  terminated: "alert",
  // Requests and documents. A decision that has been taken is worth seeing.
  approved: "go",
  cancelled: "neutral",
  withdrawn: "neutral",
  sent: "info",
  completed: "go",
  signed: "go",
  declined: "alert",
  // A monthly KPI on its way through. "Approved" above is the end of it.
  not_started: "neutral",
  in_progress: "info",
  submitted: "pending",
  returned: "alert",
  // Imports and jobs
  processing: "info",
  failed: "alert",
  success: "go",
  running: "info",
};

/** Flat fills, no ring: a chip is a label, not a control. */
const TONE_CLASS: Record<StatusTone, string> = {
  /** The ordinary case: present, legible, and not asking for attention. */
  quiet: "bg-transparent px-0 text-fg-muted",
  neutral: "bg-ink-100 text-fg-muted",
  info: "bg-iris-50 text-iris-700",
  pending: "bg-ochre-50 text-ochre-700",
  go: "bg-go-50 text-go-700",
  alert: "bg-alert-50 text-alert-700",
  brand: "bg-brand-50 text-brand-700",
};

/** The dot ahead of the word. Colour is read before the word is. */
const DOT_CLASS: Record<StatusTone, string> = {
  quiet: "bg-fg-placeholder",
  neutral: "bg-ink-400",
  info: "bg-iris-500",
  pending: "bg-ochre-500",
  go: "bg-go-500",
  alert: "bg-alert-500",
  brand: "bg-brand-500",
};

export interface StatusChipProps {
  status: string;
  /** Overrides the tone lookup for a status word this map does not know. */
  tone?: StatusTone;
  /** Human label. Defaults to the status word with underscores removed. */
  children?: string;
  className?: string;
}

export function StatusChip({ status, tone, children, className }: StatusChipProps) {
  const resolved = tone ?? TONE_BY_STATUS[status] ?? "neutral";
  const label = children ?? humanise(status);

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full whitespace-nowrap",
        // The quiet tone sets its own padding to nothing: a chip with no fill
        // should sit on the baseline of the column, not float in a gap. The
        // rest are a status pill: small capitals, a little tracking,
        // a full radius. Capitals at this size read as large as the 13px
        // sentence case they replaced, which is why they are allowed under
        // the twelve-pixel floor.
        resolved === "quiet"
          ? "py-1 text-xs font-medium"
          : "px-2 py-0.5 text-[0.6875rem] leading-[1.125rem] font-semibold uppercase tracking-[0.03em]",
        TONE_CLASS[resolved],
        className,
      )}
    >
      {resolved === "quiet" ? null : (
        <span className={cn("size-1.5 shrink-0 rounded-full", DOT_CLASS[resolved])} aria-hidden="true" />
      )}
      {label}
    </span>
  );
}

function humanise(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export { TONE_BY_STATUS };
